import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  duration?: number
}

interface UIState {
  // Theme
  theme: 'light' | 'dark' | 'system'
  
  // Font & Display
  fontSize: 'small' | 'normal' | 'large' | 'xlarge'
  reducedMotion: boolean
  highContrast: boolean
  focusIndicator: boolean
  
  // Navigation
  sidebarOpen: boolean
  sidebarCollapsed: boolean
  
  // Modals
  activeModal: string | null
  modalData: Record<string, unknown>
  
  // Notifications
  notifications: Notification[]
  
  // Loading states
  globalLoading: boolean
  
  // Actions
  setTheme: (theme: 'light' | 'dark' | 'system') => void
  setFontSize: (size: 'small' | 'normal' | 'large' | 'xlarge') => void
  setReducedMotion: (enabled: boolean) => void
  setHighContrast: (enabled: boolean) => void
  setFocusIndicator: (enabled: boolean) => void
  toggleSidebar: () => void
  collapseSidebar: (collapsed: boolean) => void
  openModal: (modalId: string, data?: unknown) => void
  closeModal: () => void
  addNotification: (notification: Omit<Notification, 'id'>) => void
  removeNotification: (id: string) => void
  setGlobalLoading: (loading: boolean) => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: 'system',
      fontSize: 'normal',
      reducedMotion: false,
      highContrast: false,
      focusIndicator: false,
      sidebarOpen: true,
      sidebarCollapsed: false,
      activeModal: null,
      modalData: {},
      notifications: [],
      globalLoading: false,
      
      setTheme: (theme) => set({ theme }),
      setFontSize: (fontSize) => set({ fontSize }),
      setReducedMotion: (reducedMotion) => set({ reducedMotion }),
      setHighContrast: (highContrast) => set({ highContrast }),
      setFocusIndicator: (focusIndicator) => set({ focusIndicator }),
      
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      
      collapseSidebar: (collapsed) => set({ sidebarCollapsed: collapsed }),
      
      openModal: (modalId, data) => set({
        activeModal: modalId,
        modalData: (data || {}) as Record<string, unknown>,
      }),
      
      closeModal: () => set({ activeModal: null, modalData: {} }),
      
      addNotification: (notification) => {
        const id = crypto.randomUUID()
        set((state) => ({
          notifications: [...state.notifications, { ...notification, id }],
        }))
        
        // Auto-dismiss after duration
        if (notification.duration) {
          setTimeout(() => {
            set((state) => ({
              notifications: state.notifications.filter((n) => n.id !== id),
            }))
          }, notification.duration)
        }
      },
      
      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),
      
      setGlobalLoading: (loading) => set({ globalLoading: loading }),
    }),
    {
      name: 'ui-preferences',
      partialize: (state) => ({ 
        theme: state.theme, 
        sidebarCollapsed: state.sidebarCollapsed,
        fontSize: state.fontSize,
        reducedMotion: state.reducedMotion,
        highContrast: state.highContrast,
        focusIndicator: state.focusIndicator,
      }),
    }
  )
)

export default useUIStore
